import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureMessage } from '@/lib/sentry';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  MessageCircle, 
  Instagram, 
  MessagesSquare, 
  Send, 
  Globe,
  MoreVertical,
  Settings,
  Trash2,
  Power,
  PowerOff,
  QrCode,
  RefreshCw,
  Webhook,
  Phone,
  Wifi,
  WifiOff,
  Loader2,
  AlertCircle,
  Unplug,
  Clock,
  PauseCircle,
  Star,
  ArrowRightLeft,
  Link2,
  Unlink,
  Eye
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatInboxIdentifierForDisplay } from '@/lib/phone-utils';

// Tipos de status da inbox - inclui pending_setup para OAuth WhatsApp Oficial
export type InboxStatus = 'pending' | 'pending_setup' | 'active' | 'inactive' | 'connecting' | 'error' | 'disconnected' | 'suspended';
export type ChannelType = 'whatsapp' | 'whatsapp_oficial' | 'instagram' | 'messenger' | 'telegram' | 'webchat';

interface Inbox {
  id: string;
  name: string;
  channel_type: ChannelType;
  identifier: string;
  status: InboxStatus;
  is_default?: boolean;
  provider?: {
    name: string;
    display_name?: string;
    logo_url?: string;
    slug?: string;  // Slug do provider para identificação (ex: 'meta-whatsapp', 'z-api')
  };
  provider_version?: {
    version: string;
    status: string;
  };
}

/**
 * Interface para informações de migração pendente
 * Inclui display_name para ocultar nomes técnicos do tenant
 */
export interface PendingMigrationInfo {
  new_provider_slug: string;
  old_provider_slug: string;
  /** Nome amigável do provider novo (vem do banco) */
  new_provider_display_name?: string | null;
  /** Nome amigável do provider antigo (vem do banco) */
  old_provider_display_name?: string | null;
  reason?: string;
}

/**
 * Props do componente InboxCard
 * Inclui permissões granulares para controlar visibilidade de ações
 */
interface InboxCardProps {
  inbox: Inbox;
  onEdit: (inbox: Inbox) => void;
  onDelete: (inbox: Inbox) => void;
  onToggleStatus: (inbox: Inbox) => void;
  onConnect?: (inbox: Inbox) => void;
  onCheckStatus?: (inbox: Inbox) => void;
  onConfigureWebhooks?: (inbox: Inbox) => void;
  onDisconnect?: (inbox: Inbox) => void;
  onSetDefault?: (inbox: Inbox) => void;
  onConfigureWebchat?: (inbox: Inbox) => void;
  /** Callback para conexão OAuth (WhatsApp Oficial) */
  onConnectOAuth?: (inbox: Inbox) => void;
  /** Callback para desvincular instância */
  onUnlinkInstance?: (inbox: Inbox) => void;
  /** Callback para vincular instância */
  onLinkInstance?: (inbox: Inbox) => void;
  /** Callback para abrir o modal de detalhes da inbox */
  onViewDetails?: (inbox: Inbox) => void;
  // Permissões granulares (default: true para manter compatibilidade)
  canEditInbox?: boolean;
  canDeleteInbox?: boolean;
  /** Indica se há migração pendente para esta inbox */
  hasPendingMigration?: boolean;
  /** Informações da migração pendente */
  migrationInfo?: PendingMigrationInfo;
}

// Configuração de cores por tipo de canal - borda superior
const channelBorderColors: Record<ChannelType, string> = {
  whatsapp: 'border-t-green-500',
  whatsapp_oficial: 'border-t-emerald-500',
  instagram: 'border-t-pink-500',
  messenger: 'border-t-blue-500',
  telegram: 'border-t-sky-400',
  webchat: 'border-t-muted-foreground',
};

// Configuração de cores de fundo do ícone por canal
const channelBgColors: Record<ChannelType, string> = {
  whatsapp: 'bg-green-500/10',
  whatsapp_oficial: 'bg-emerald-500/10',
  instagram: 'bg-gradient-to-br from-pink-500/10 to-purple-500/10',
  messenger: 'bg-blue-500/10',
  telegram: 'bg-sky-400/10',
  webchat: 'bg-muted',
};

// Configuração de cores do ícone por canal
const channelIconColors: Record<ChannelType, string> = {
  whatsapp: 'text-green-600 dark:text-green-400',
  whatsapp_oficial: 'text-emerald-600 dark:text-emerald-400',
  instagram: 'text-pink-600 dark:text-pink-400',
  messenger: 'text-blue-600 dark:text-blue-400',
  telegram: 'text-sky-500 dark:text-sky-400',
  webchat: 'text-muted-foreground',
};

// Configuração de ícones e labels por canal
const channelConfig: Record<ChannelType, { icon: typeof MessageCircle; label: string }> = {
  whatsapp: { icon: MessageCircle, label: 'WhatsApp' },
  whatsapp_oficial: { icon: MessageCircle, label: 'WhatsApp Oficial' },
  instagram: { icon: Instagram, label: 'Instagram' },
  messenger: { icon: MessagesSquare, label: 'Messenger' },
  telegram: { icon: Send, label: 'Telegram' },
  webchat: { icon: Globe, label: 'WebChat' },
};

// Configuração de estilos de status
// Configuração de estilos de status - inclui pending_setup para OAuth WhatsApp Oficial
const statusStyles: Record<InboxStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  pending_setup: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  active: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  inactive: 'bg-muted text-muted-foreground border-muted',
  connecting: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  error: 'bg-destructive/10 text-destructive border-destructive/20',
  disconnected: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  suspended: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
};

// Configuração de ícones de status (WiFi icons) - inclui pending_setup
const statusIcons: Record<InboxStatus, typeof Wifi> = {
  pending: Clock,
  pending_setup: Clock,
  active: Wifi,
  inactive: WifiOff,
  connecting: Loader2,
  error: AlertCircle,
  disconnected: WifiOff,
  suspended: PauseCircle,
};

// Cores apenas do ícone de status (badge simplificado: ícone colorido + texto padrão)
const statusIconColors: Record<InboxStatus, string> = {
  pending: 'text-amber-600 dark:text-amber-400',
  pending_setup: 'text-blue-600 dark:text-blue-400',
  active: 'text-green-600 dark:text-green-400',
  inactive: 'text-muted-foreground',
  connecting: 'text-blue-600 dark:text-blue-400',
  error: 'text-destructive',
  disconnected: 'text-orange-600 dark:text-orange-400',
  suspended: 'text-purple-600 dark:text-purple-400',
};

// Configuração de animação dos ícones de status
// Configuração de animação dos ícones de status - inclui pending_setup
const statusIconAnimation: Record<InboxStatus, string> = {
  pending: '',
  pending_setup: '',
  active: '',
  inactive: '',
  connecting: 'animate-spin',
  error: '',
  disconnected: '',
  suspended: '',
};

// Labels de status - inclui pending_setup para OAuth WhatsApp Oficial
const statusLabels: Record<InboxStatus, string> = {
  pending: 'Pendente',
  pending_setup: 'Aguardando Ativação',
  active: 'Conectado',
  inactive: 'Inativo',
  connecting: 'Conectando...',
  error: 'Erro',
  disconnected: 'Desconectado',
  suspended: 'Suspensa',
};

// Gradiente da barra superior do card por status (acento "premium")
const statusAccentGradient: Record<InboxStatus, string> = {
  pending: 'bg-gradient-to-r from-amber-500 to-amber-300',
  pending_setup: 'bg-gradient-to-r from-blue-500 to-blue-300',
  active: 'bg-gradient-to-r from-emerald-500 to-green-400',
  inactive: 'bg-gradient-to-r from-muted-foreground/40 to-muted-foreground/20',
  connecting: 'bg-gradient-to-r from-blue-500 to-sky-300',
  error: 'bg-gradient-to-r from-rose-500 to-red-400',
  disconnected: 'bg-gradient-to-r from-orange-500 to-amber-300',
  suspended: 'bg-gradient-to-r from-purple-500 to-fuchsia-400',
};

// Cor de fundo do "bloom" decorativo (blur) por status
const statusBloomBg: Record<InboxStatus, string> = {
  pending: 'bg-amber-500/10',
  pending_setup: 'bg-blue-500/10',
  active: 'bg-emerald-500/10',
  inactive: 'bg-muted',
  connecting: 'bg-blue-500/10',
  error: 'bg-rose-500/10',
  disconnected: 'bg-orange-500/10',
  suspended: 'bg-purple-500/10',
};

// Cor sólida do dot de status (overlay no ícone do canal)
const statusDotBg: Record<InboxStatus, string> = {
  pending: 'bg-amber-500',
  pending_setup: 'bg-blue-500',
  active: 'bg-emerald-500',
  inactive: 'bg-muted-foreground',
  connecting: 'bg-blue-500',
  error: 'bg-rose-500',
  disconnected: 'bg-orange-500',
  suspended: 'bg-purple-500',
};

/**
 * Componente de card moderno para exibição de inbox
 * Inclui borda colorida por canal, shimmer effect e status elegante
 */

export function InboxCard({ 
  inbox, 
  onEdit, 
  onDelete, 
  onToggleStatus, 
  onConnect, 
  onCheckStatus, 
  onConfigureWebhooks,
  onDisconnect,
  onSetDefault,
  onConfigureWebchat,
  onConnectOAuth,
  onUnlinkInstance,
  onLinkInstance,
  onViewDetails,
  canEditInbox = true,
  canDeleteInbox = true,
  hasPendingMigration = false,
  migrationInfo,
}: InboxCardProps) {
  const navigate = useNavigate();
  // Fallbacks defensivos: evita "Element type is invalid" quando o backend/realtime
  // entrega um channel_type ou status que ainda não está mapeado no front (drift de enum,
  // update otimista transitório, migration nova, HMR aplicando código novo sobre estado antigo).
  const channel = channelConfig[inbox.channel_type] ?? { icon: Globe, label: 'Canal' };
  const ChannelIcon = channel.icon ?? Globe;
  const channelBg = channelBgColors[inbox.channel_type] ?? 'bg-muted';
  const channelIconColor = channelIconColors[inbox.channel_type] ?? 'text-muted-foreground';
  const channelBorder = channelBorderColors[inbox.channel_type] ?? 'border-t-muted-foreground';

  // Reporta ao Sentry (apenas em PROD; sem console/network local) quando um enum
  // desconhecido for renderizado, para descobrirmos o valor real causando o crash histórico.
  useEffect(() => {
    if (inbox.channel_type && !channelConfig[inbox.channel_type]) {
      captureMessage('InboxCard: channel_type desconhecido', 'warning', {
        tags: { component: 'InboxCard', field: 'channel_type' },
        extra: {
          inboxId: inbox.id,
          value: inbox.channel_type,
          knownKeys: Object.keys(channelConfig),
        },
      });
    }
    if (inbox.status && !statusIcons[inbox.status]) {
      captureMessage('InboxCard: status desconhecido', 'warning', {
        tags: { component: 'InboxCard', field: 'status' },
        extra: {
          inboxId: inbox.id,
          value: inbox.status,
          knownKeys: Object.keys(statusIcons),
        },
      });
    }
  }, [inbox.id, inbox.channel_type, inbox.status]);
  // WhatsApp não-oficial requer conexão QR, oficial não
  const isWhatsAppNonOfficial = inbox.channel_type === 'whatsapp';
  // WhatsApp Oficial requer OAuth
  const isWhatsAppOficial = inbox.channel_type === 'whatsapp_oficial';
  // Provider Meta WABA permite reconexão via Embedded Signup
  const isMetaWhatsApp = inbox.provider?.slug === 'meta-whatsapp';
  // WebChat tem configurações específicas
  const isWebchat = inbox.channel_type === 'webchat';
  // Inbox suspensa não pode executar ações
  const isSuspended = inbox.status === 'suspended';
  // Inbox inativa não pode executar ações de conexão - deve ativar primeiro
  const isInactive = inbox.status === 'inactive';
  // Conexão QR para WhatsApp não-oficial (inclui 'pending': inbox recém-criada
  // com instância vinculada ainda precisa ler o QR Code para conectar)
  const needsConnection = isWhatsAppNonOfficial && ['pending', 'disconnected', 'error'].includes(inbox.status);
  // Conexão OAuth para WhatsApp Oficial (status pending ou disconnected)
  const needsOAuthConnection = isWhatsAppOficial && ['pending', 'disconnected'].includes(inbox.status);
  // Verifica se tem instância vinculada (para exibir opção de desvincular/vincular)
  const hasLinkedInstance = !!(inbox as any).linked_instance;
  
  // Ações de provider (QR, status, webhooks) habilitadas para status operacionais
  // Status permitidos: active, pending, connecting, error, disconnected
  const canExecuteProviderActions = 
    ['active', 'pending', 'connecting', 'error', 'disconnected'].includes(inbox.status) && 
    hasLinkedInstance;

  // Handler para abrir o modal de detalhes (fallback: rota antiga)
  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(inbox);
      return;
    }
    navigate(`/tenant/inboxes/${inbox.id}`);
  };

  // Estilos derivados do status para o novo layout "Premium com profundidade"
  const accentGradient = statusAccentGradient[inbox.status] ?? 'bg-gradient-to-r from-muted-foreground/40 to-muted-foreground/20';
  const bloomBg = statusBloomBg[inbox.status] ?? 'bg-muted';
  const dotBg = statusDotBg[inbox.status] ?? 'bg-muted-foreground';
  const StatusIcon = statusIcons[inbox.status] ?? AlertCircle;
  const statusColor = statusIconColors[inbox.status] ?? 'text-muted-foreground';
  const statusAnim = statusIconAnimation[inbox.status] ?? '';
  const statusLabel = statusLabels[inbox.status] ?? 'Desconhecido';
  const pulseDot = ['connecting', 'error', 'pending', 'pending_setup'].includes(inbox.status);

  return (
    <div className="group relative w-full min-w-0">
      {/* Barra de status superior com gradiente */}
      <div className={cn(
        "absolute top-0 inset-x-0 h-1.5 rounded-t-2xl z-10",
        accentGradient
      )} />

      {/* Container principal do card */}
      <div className={cn(
        "relative overflow-hidden bg-card text-card-foreground border border-border/60 rounded-2xl",
        "shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300",
        "hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1"
      )}>
        {/* Bloom decorativo (gradient blur) */}
        <div className={cn(
          "pointer-events-none absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl opacity-60",
          bloomBg
        )} />

        <div className="relative p-4 sm:p-5 space-y-4 sm:space-y-5">
          {/* === HEADER === */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex gap-3 min-w-0 flex-1">
              {/* Ícone do canal + dot de status */}
              <div className="relative shrink-0">
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center border border-border/40 shadow-sm transition-transform group-hover:scale-105",
                  channelBg
                )}>
                  <ChannelIcon className={cn("w-6 h-6", channelIconColor)} />
                </div>
                <div className={cn(
                  "absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card flex items-center justify-center",
                  dotBg
                )}>
                  {pulseDot && (
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  )}
                </div>
              </div>

              {/* Nome + Badge ID + label do canal */}
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold text-foreground leading-tight truncate">
                    {inbox.name}
                  </h3>
                  <Badge
                    variant="outline"
                    className="font-mono text-[10px] px-1.5 py-0 h-5 text-muted-foreground border-border/60 bg-muted/50"
                  >
                    {inbox.id.substring(0, 8).toUpperCase()}
                  </Badge>
                  {inbox.is_default && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">
                            <Star className="h-3 w-3 mr-0.5 fill-current" />
                            Padrão
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Esta inbox será mantida ativa em caso de downgrade</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <p className="text-xs text-muted-foreground font-medium truncate">{channel.label}</p>
              </div>
            </div>

            {/* Ações: Star + Menu */}
            <div className="flex items-center gap-0.5 shrink-0">
              {canEditInbox && !inbox.is_default && !isSuspended && onSetDefault && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="hidden sm:inline-flex h-8 w-8 text-muted-foreground hover:text-amber-500"
                        onClick={() => onSetDefault(inbox)}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Definir como padrão</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* Ações de WhatsApp - permissão de edição */}
                  {isWhatsAppNonOfficial && canEditInbox && (
                    <>
                      <DropdownMenuItem
                        onClick={() => canExecuteProviderActions && onConnect?.(inbox)}
                        disabled={!canExecuteProviderActions}
                        className={!canExecuteProviderActions ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <QrCode className="h-4 w-4 mr-2" />
                        Conectar WhatsApp
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => canExecuteProviderActions && onCheckStatus?.(inbox)}
                        disabled={!canExecuteProviderActions}
                        className={!canExecuteProviderActions ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Verificar Status
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => canExecuteProviderActions && onConfigureWebhooks?.(inbox)}
                        disabled={!canExecuteProviderActions}
                        className={!canExecuteProviderActions ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <Webhook className="h-4 w-4 mr-2" />
                        Configurar Webhooks
                      </DropdownMenuItem>
                      {inbox.status === 'active' && (
                        <DropdownMenuItem onClick={() => onDisconnect?.(inbox)}>
                          <Unplug className="h-4 w-4 mr-2" />
                          Desconectar
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  {isWhatsAppOficial && isMetaWhatsApp && canEditInbox && (
                    <>
                      {['active', 'error', 'disconnected'].includes(inbox.status) && (
                        <DropdownMenuItem onClick={() => onConnectOAuth?.(inbox)}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Reconectar WhatsApp Business
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onCheckStatus?.(inbox)}
                        disabled={!['active', 'connecting'].includes(inbox.status)}
                        className={!['active', 'connecting'].includes(inbox.status) ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Verificar Status
                      </DropdownMenuItem>
                      {inbox.status === 'active' && (
                        <DropdownMenuItem onClick={() => onDisconnect?.(inbox)}>
                          <Unplug className="h-4 w-4 mr-2" />
                          Desconectar
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  {isWebchat && canEditInbox && onConfigureWebchat && (
                    <DropdownMenuItem onClick={() => onConfigureWebchat(inbox)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Configurar Widget
                    </DropdownMenuItem>
                  )}

                  {canEditInbox && !isWebchat && <DropdownMenuSeparator />}

                  {canEditInbox && !isWebchat && (
                    <>
                      {hasLinkedInstance ? (
                        <DropdownMenuItem
                          onClick={() => onUnlinkInstance?.(inbox)}
                          className="text-amber-600 dark:text-amber-400"
                        >
                          <Unlink className="h-4 w-4 mr-2" />
                          Desvincular Instância
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => onLinkInstance?.(inbox)}>
                          <Link2 className="h-4 w-4 mr-2" />
                          Vincular Instância
                        </DropdownMenuItem>
                      )}
                    </>
                  )}

                  <DropdownMenuSeparator />

                  <DropdownMenuItem onClick={handleViewDetails}>
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalhes
                  </DropdownMenuItem>

                  {canEditInbox && (
                    <DropdownMenuItem onClick={() => onEdit(inbox)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Configurações
                    </DropdownMenuItem>
                  )}
                  {canEditInbox && !isSuspended && (
                    <DropdownMenuItem onClick={() => onToggleStatus(inbox)}>
                      {inbox.status === 'inactive' ? (
                        <>
                          <Power className="h-4 w-4 mr-2" />
                          Ativar
                        </>
                      ) : (
                        <>
                          <PowerOff className="h-4 w-4 mr-2" />
                          Desativar
                        </>
                      )}
                    </DropdownMenuItem>
                  )}
                  {canEditInbox && isSuspended && (
                    <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Suspensa - Faça upgrade
                    </DropdownMenuItem>
                  )}
                  {canDeleteInbox && (
                    <DropdownMenuItem
                      onClick={() => onDelete(inbox)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* === TELEFONE / IDENTIFICADOR === */}
          <div className="flex items-center gap-2 text-sm font-medium text-foreground/80 min-w-0">
            <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-mono truncate tracking-tight">
              {formatInboxIdentifierForDisplay(inbox.channel_type, inbox.identifier)}
            </span>
          </div>

          {/* === METADADOS + STATUS === */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {inbox.provider && (
                <>
                  <Badge variant="outline" className="text-[10px] font-semibold bg-muted/60 border-border/60 text-muted-foreground rounded-full px-2.5 py-0.5">
                    via {inbox.provider.display_name || inbox.provider.name}
                  </Badge>
                  {inbox.provider_version && (
                    <Badge variant="outline" className="text-[10px] font-mono bg-muted/40 border-border/50 text-muted-foreground rounded-full px-2 py-0.5">
                      v{inbox.provider_version.version}
                    </Badge>
                  )}
                </>
              )}

              {hasPendingMigration && migrationInfo && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 animate-pulse rounded-full"
                      >
                        <ArrowRightLeft className="h-3 w-3 mr-1" />
                        Migrar para {migrationInfo.new_provider_display_name || migrationInfo.new_provider_slug}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Migração pendente: {migrationInfo.old_provider_display_name || migrationInfo.old_provider_slug} → {migrationInfo.new_provider_display_name || migrationInfo.new_provider_slug}</p>
                      {migrationInfo.reason && <p className="text-xs text-muted-foreground">Motivo: {migrationInfo.reason}</p>}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Status com dot + label */}
            <div className="flex items-center gap-2">
              <span className={cn(
                "w-1.5 h-1.5 rounded-full shadow-[0_0_8px_currentColor]",
                dotBg,
                statusColor
              )} />
              <StatusIcon className={cn("h-3.5 w-3.5", statusColor, statusAnim)} />
              <span className={cn("text-[11px] font-bold uppercase tracking-widest", statusColor)}>
                {statusLabel}
              </span>
            </div>
          </div>

          {/* === BOTÕES DE CONEXÃO === */}
          {needsConnection && onConnect && canExecuteProviderActions && (
            <button
              onClick={() => onConnect(inbox)}
              className="w-full group/btn relative flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 text-white text-sm font-semibold overflow-hidden transition-all active:scale-[0.98] hover:opacity-95 shadow-sm"
            >
              {/* Brilho deslizante no hover */}
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
              <QrCode className="h-4 w-4" />
              Conectar WhatsApp
            </button>
          )}

          {needsOAuthConnection && onConnectOAuth && canEditInbox && (
            <button
              onClick={() => onConnectOAuth(inbox)}
              className="w-full group/btn relative flex items-center justify-center gap-2 py-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400 text-sm font-semibold overflow-hidden transition-all hover:bg-emerald-500/10 hover:border-emerald-500/50 active:scale-[0.98]"
            >
              <MessageCircle className="h-4 w-4" />
              Conectar WhatsApp Business
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
